import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { SigilMark } from "../components/Sigil";
import { useAuth, formatApiError } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      navigate("/sanctum");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen bg-ink flex flex-col items-center justify-center px-6"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="w-full max-w-md"
      >
        <div className="flex flex-col items-center mb-10 text-gold">
          <SigilMark className="w-16 h-16" spin />
          <h1 className="font-display text-2xl text-parchment mt-6 tracking-wide">
            The Inner Sanctum
          </h1>
          <p className="text-xs text-parchment-muted mt-2 font-mono tracking-widest uppercase">
            Practitioners only beyond this seal
          </p>
        </div>

        <form
          data-testid="login-form"
          onSubmit={submit}
          className="parchment-card rounded-2xl p-8 sm:p-10 space-y-6"
        >
          <div>
            <label htmlFor="login-email" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2.5">
              Email
            </label>
            <input
              id="login-email"
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-ink/60 border border-gold/25 rounded-lg px-4 py-3.5 text-sm text-parchment focus:outline-none focus:border-gold/70 transition-colors"
            />
          </div>
          <div>
            <label htmlFor="login-password" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2.5">
              Password
            </label>
            <input
              id="login-password"
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-ink/60 border border-gold/25 rounded-lg px-4 py-3.5 text-sm text-parchment focus:outline-none focus:border-gold/70 transition-colors"
            />
          </div>

          {error && (
            <p data-testid="login-error" className="text-sm text-red-400/90">
              {error}
            </p>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-gold text-ink rounded-full py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-amber transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Opening the seal…
              </>
            ) : (
              "Enter the Sanctum"
            )}
          </button>
        </form>

        <div className="text-center mt-8">
          <Link
            to="/"
            data-testid="login-home-link"
            className="text-xs text-parchment-muted hover:text-gold transition-colors font-mono tracking-widest uppercase"
          >
            ← Return to the outer gates
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
